import React, { useMemo } from 'react'
import type { CSSProperties } from 'react'
import type { ProgressBarProps } from './ProgressBar.types'
import { PROGRESS_BAR_PREVIEW_VALUES } from './ProgressBar.types'
import './ProgressBar.css'

const clampPercent = (value: number): number =>
  Math.min(100, Math.max(0, Number.isFinite(value) ? value : 0))

const ProgressBar: React.FC<ProgressBarProps> = ({
  value = 0,
  label,
  hideLabel = false,
  darkMode: _darkMode = false,
  className = '',
  style,
  previewState,
  'aria-label': ariaLabel,
}) => {
  const resolvedValue = useMemo(() => {
    if (previewState) {
      return PROGRESS_BAR_PREVIEW_VALUES[previewState]
    }
    return clampPercent(value)
  }, [previewState, value])

  const displayLabel = label ?? `${Math.round(resolvedValue)}%`
  const fillPercent = `${resolvedValue}%`

  const classNames = [
    'ds-progress-bar',
    previewState ? 'ds-progress-bar--preview' : '',
    previewState ? `ds-progress-bar--preview-${previewState}` : '',
    resolvedValue <= 0 ? 'ds-progress-bar--empty' : '',
    resolvedValue >= 100 ? 'ds-progress-bar--complete' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const rootStyle = {
    ...style,
    '--ds-progress-bar-value': fillPercent,
  } as CSSProperties

  return (
    <div
      className={classNames}
      style={rootStyle}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(resolvedValue)}
      aria-label={ariaLabel ?? 'Progress'}
    >
      <div
        className="ds-progress-bar__fill"
        style={{ width: fillPercent }}
        aria-hidden="true"
      />
      {!hideLabel ? (
        <>
          <span className="ds-progress-bar__label ds-progress-bar__label--base">
            {displayLabel}
          </span>
          <span
            className="ds-progress-bar__label ds-progress-bar__label--on-fill"
            aria-hidden="true"
          >
            {displayLabel}
          </span>
        </>
      ) : null}
    </div>
  )
}

export default ProgressBar
